import { lazy } from 'react';

/**
 * React.lazy that survives a deploy landing under an already-open tab.
 *
 * Every route in App.jsx is code-split, so its chunk is fetched only when the
 * user first opens that route. Vite gives each chunk a content-hashed filename,
 * and a deploy replaces the whole `assets/` directory — so a tab still running
 * the PREVIOUS build asks for a filename the origin no longer has, the dynamic
 * import rejects, and the ErrorBoundary shows "Something went wrong on this
 * page" (reported 2026-08-27 on /new-referral after the .133 cutover).
 *
 * The service worker already handles the tabs it controls: sw.js is stamped
 * with a fresh BUILD_ID each build, and index.jsx reloads on `controllerchange`.
 * Neither helps here, because the failure is discovered by an already-loaded
 * page at import time. So: reload once — that re-fetches index.html and with it
 * the current hashes — and record the attempt in sessionStorage so a chunk that
 * is genuinely missing (a bad build) falls through to the ErrorBoundary instead
 * of looping the tab.
 */

export const CHUNK_RELOAD_KEY = 'afc:chunk-reload';

// One per engine — the browsers word this rejection differently.
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module', // Chrome / Edge
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // Safari
  'failed to load module script', // Chrome, when the 404 body is served as HTML
];

export function isChunkLoadError(error) {
  const message = String(error?.message || error || '').toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

function sessionStorageOrNull() {
  try {
    return window.sessionStorage;
  } catch {
    // Safari in private mode and some embedded webviews throw on access.
    return null;
  }
}

/**
 * Wraps a dynamic `import()` with the one-shot reload described above.
 * `storage` and `reload` are injected so the behaviour is testable in node.
 */
export function retryImport(factory, { storage, reload } = {}) {
  return Promise.resolve()
    .then(factory)
    .then((mod) => {
      // A chunk loaded, so this tab is on the current build. Clear the guard so
      // the NEXT deploy gets its own reload rather than going straight to the
      // error card.
      try {
        storage?.removeItem(CHUNK_RELOAD_KEY);
      } catch {
        /* the guard is best-effort; a stale flag only costs one error card */
      }
      return mod;
    })
    .catch((error) => {
      if (!isChunkLoadError(error)) throw error;

      // No writable guard means no way to stop a second failure from reloading
      // again — an endless refresh is far worse for the user than the error
      // card, which at least has a Reload button and the details to report.
      if (!storage) throw error;
      try {
        if (storage.getItem(CHUNK_RELOAD_KEY) === '1') throw error;
        storage.setItem(CHUNK_RELOAD_KEY, '1');
      } catch {
        throw error;
      }

      reload();
      // Deliberately never settles: the reload is already replacing this
      // document, and resolving or rejecting here would flash the error card
      // (or an empty route) in the moments before it does.
      return new Promise(() => {});
    });
}

export default function lazyWithRetry(factory) {
  return lazy(() =>
    retryImport(factory, {
      storage: sessionStorageOrNull(),
      reload: () => window.location.reload(),
    })
  );
}
